import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, FileSearch, RefreshCw, Inbox, TrendingUp, Globe2, Users } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const lacs = (v) => (v == null ? "—" : (Number(v) / 100).toFixed(2));
const inr = (v) => (v == null ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 }));
const cr = (v) => (v == null ? "—" : Number(v).toFixed(2));
const dateFmt = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

function KpiCard({ label, value, sub, testId }) {
  return (
    <Card className="p-4" data-testid={testId}>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="font-mono text-2xl font-bold mt-1">{value}</p>
      {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
    </Card>
  );
}

function SummaryTable({ title, rows, nameLabel, testId }) {
  if (!rows?.length) return null;
  return (
    <Card className="overflow-hidden" data-testid={testId}>
      <p className="px-4 pt-3 pb-1 text-sm font-heading font-semibold">{title}</p>
      <Table>
        <TableHeader><TableRow className="bg-slate-50">
          {[nameLabel, "Day (Cr)", "Month (Cr)", "Year (Cr)"].map((h) => (
            <TableHead key={h} className="text-[11px] uppercase tracking-wider text-slate-500">{h}</TableHead>))}
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i} className={r.name.toLowerCase() === "total" ? "font-semibold bg-slate-50/60" : ""}>
              <TableCell className="text-xs">{r.name}</TableCell>
              <TableCell className="font-mono text-xs text-right">{lacs(r.day)}</TableCell>
              <TableCell className="font-mono text-xs text-right">{lacs(r.month)}</TableCell>
              <TableCell className="font-mono text-xs text-right">{lacs(r.year)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function OrdersTable({ orders, isExport, testId }) {
  if (!orders?.length) {
    return (
      <Card className="p-8 text-center text-sm text-slate-400" data-testid={`${testId}-empty`}>
        No {isExport ? "export" : "domestic"} orders booked this day.
      </Card>
    );
  }
  return (
    <Card className="overflow-hidden" data-testid={testId}>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            {["S.O. No.", "Date", "Sales Mgr.", "Client", isExport ? "Country" : "Project",
              "Items", "S.O. Total", isExport ? "" : "Industry"].filter(Boolean).map((h) => (
              <TableHead key={h} className="text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>))}
          </TableRow></TableHeader>
          <TableBody>
            {orders.map((so, i) => (
              <TableRow key={i} data-testid={`${testId}-row-${i}`}>
                <TableCell className="font-mono text-xs whitespace-nowrap align-top">{so.so_no || "—"}</TableCell>
                <TableCell className="text-xs whitespace-nowrap align-top">{so.so_date}</TableCell>
                <TableCell className="text-xs whitespace-nowrap align-top">{so.sales_mgr}</TableCell>
                <TableCell className="text-xs max-w-[180px] align-top">{so.client_name}</TableCell>
                <TableCell className="text-xs max-w-[160px] align-top text-slate-500">
                  {isExport ? so.country_name : so.project_name}
                </TableCell>
                <TableCell className="text-xs align-top">
                  <div className="space-y-1">
                    {so.items.map((it, k) => (
                      <div key={k} className="flex items-baseline gap-2">
                        <span className="text-slate-600 max-w-[260px]">{it.description}</span>
                        <span className="font-mono text-slate-400 whitespace-nowrap">
                          {it.quantity != null ? `${it.quantity} ${it.uom}` : ""} · {inr(it.item_total)}
                        </span>
                      </div>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="font-mono text-xs font-semibold text-right whitespace-nowrap align-top">{inr(so.so_total)}</TableCell>
                {!isExport && <TableCell className="text-xs align-top text-slate-500">{so.industry_type}</TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function PerfTable({ rows, testId, title }) {
  if (!rows?.length) return null;
  return (
    <Card className="overflow-hidden" data-testid={testId}>
      <p className="px-4 pt-3 pb-1 text-sm font-heading font-semibold flex items-center gap-1.5"><Users className="h-4 w-4 text-slate-400" /> {title}</p>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            {["Employee", "Yr Target (Cr)", "Yr Achieved (Cr)", "Yr %", "AsOn Target", "AsOn Achieved", "AsOn %"].map((h) => (
              <TableHead key={h} className="text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>))}
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={i}>
                <TableCell className="text-xs">{r.name}</TableCell>
                <TableCell className="font-mono text-xs text-right">{cr(r.target_yr)}</TableCell>
                <TableCell className="font-mono text-xs text-right">{cr(r.achieved_yr)}</TableCell>
                <TableCell className="font-mono text-xs text-right">{cr(r.pct_yr)}</TableCell>
                <TableCell className="font-mono text-xs text-right">{cr(r.target_ason)}</TableCell>
                <TableCell className="font-mono text-xs text-right">{cr(r.achieved_ason)}</TableCell>
                <TableCell className="font-mono text-xs text-right">{cr(r.pct_ason)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function PairList({ title, rows, testId }) {
  if (!rows?.length) return null;
  return (
    <Card className="p-4" data-testid={testId}>
      <p className="text-sm font-heading font-semibold mb-2">{title}</p>
      <div className="space-y-1">
        {rows.map((r, i) => (
          <div key={i} className="flex justify-between text-xs">
            <span className="text-slate-600">{r.name}</span>
            <span className="font-mono font-semibold">{cr(r.value_cr)}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function OrderBookingData() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [reparsing, setReparsing] = useState(false);

  const { data: list, isLoading } = useQuery({
    queryKey: ["ob-data-list"],
    queryFn: async () => (await api.get("/milestone-pdf/data")).data,
  });

  const entries = list?.data || [];
  const activeId = selected || entries[0]?.report_id;

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ["ob-data-detail", activeId],
    queryFn: async () => (await api.get(`/milestone-pdf/data/${activeId}`)).data,
    enabled: !!activeId,
  });

  const reparse = async () => {
    setReparsing(true);
    try {
      const r = await api.post(`/milestone-pdf/reports/${activeId}/reparse`);
      toast.success(`Re-extracted — status: ${r.data.status}`);
      qc.invalidateQueries({ queryKey: ["ob-data-list"] });
      qc.invalidateQueries({ queryKey: ["ob-data-detail", activeId] });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setReparsing(false);
    }
  };

  if (isLoading) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;

  if (!entries.length) {
    return (
      <Card className="p-10 text-center" data-testid="ob-data-empty">
        <Inbox className="h-8 w-8 mx-auto text-slate-300" />
        <p className="mt-3 text-sm font-medium text-slate-600">No order booking data yet</p>
        <p className="text-xs text-slate-400 mt-1">
          Figures are extracted automatically from each daily Milestone PDF received via CloudMailin (Utility → Milestone PDF).
        </p>
      </Card>
    );
  }

  const hl = detail?.headline || {};

  return (
    <div className="space-y-4" data-testid="order-booking-data">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={activeId} onValueChange={setSelected}>
          <SelectTrigger className="w-56" data-testid="ob-report-select">
            <SelectValue placeholder="Select report date" />
          </SelectTrigger>
          <SelectContent>
            {entries.map((e) => (
              <SelectItem key={e.report_id} value={e.report_id} data-testid={`ob-report-option-${e.report_id}`}>
                {dateFmt(e.report_date)} {e.status !== "parsed" ? `(${e.status})` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {detail && (
          <Badge variant="outline" className={detail.status === "parsed" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-100 text-amber-700 border-amber-200"} data-testid="ob-status-badge">
            {detail.status}
          </Badge>
        )}
        <span className="text-xs text-slate-400">{detail?.source_filename}</span>
        <Button variant="outline" size="sm" className="ml-auto" onClick={reparse} disabled={reparsing || !activeId} data-testid="ob-reparse-btn">
          {reparsing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Re-extract
        </Button>
      </div>

      {detailLoading ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : !detail ? null : detail.status !== "parsed" ? (
        <Card className="p-8 text-center" data-testid="ob-parse-error">
          <FileSearch className="h-7 w-7 mx-auto text-amber-400" />
          <p className="mt-2 text-sm font-medium text-slate-700">Couldn't extract order booking tables from this PDF</p>
          <p className="text-xs text-slate-400 mt-1">{detail.error || "Unrecognised layout"}</p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard label="Booked — Day" value={lacs(hl.day_lacs)} sub={dateFmt(detail.report_date)} testId="ob-kpi-day" />
            <KpiCard label="Booked — Month" value={lacs(hl.month_lacs)} sub="MTD" testId="ob-kpi-month" />
            <KpiCard label="Booked — Year" value={lacs(hl.year_lacs)} sub="FY to date" testId="ob-kpi-year" />
            <KpiCard label="Orders FY" value={hl.total_orders_nos ?? "—"}
              sub={hl.total_value_cr ? `${hl.total_value_cr} FY value (INR Cr) · ${hl.domestic_orders} dom / ${hl.export_orders} exp today` : ""}
              testId="ob-kpi-orders" />
          </div>

          <Tabs defaultValue="summary">
            <TabsList data-testid="ob-inner-tabs">
              <TabsTrigger value="summary" data-testid="ob-tab-summary">Summary</TabsTrigger>
              <TabsTrigger value="orders" data-testid="ob-tab-orders">Day's Orders</TabsTrigger>
              <TabsTrigger value="performance" data-testid="ob-tab-performance">Performance</TabsTrigger>
            </TabsList>

            <TabsContent value="summary" className="mt-3 grid gap-4 lg:grid-cols-3 items-start">
              <SummaryTable title="Company wise" nameLabel="Company" rows={detail.summary?.company} testId="ob-summary-company" />
              <SummaryTable title="Order type wise" nameLabel="Order Type" rows={detail.summary?.order_type} testId="ob-summary-ordertype" />
              <SummaryTable title="Region wise" nameLabel="Region" rows={detail.summary?.region} testId="ob-summary-region" />
            </TabsContent>

            <TabsContent value="orders" className="mt-3 space-y-4">
              <p className="text-sm font-heading font-semibold flex items-center gap-1.5"><TrendingUp className="h-4 w-4 text-slate-400" /> Domestic orders — {dateFmt(detail.report_date)}</p>
              <OrdersTable orders={detail.orders_domestic} isExport={false} testId="ob-orders-domestic" />
              <p className="text-sm font-heading font-semibold flex items-center gap-1.5"><Globe2 className="h-4 w-4 text-slate-400" /> Export orders</p>
              <OrdersTable orders={detail.orders_export} isExport testId="ob-orders-export" />
            </TabsContent>

            <TabsContent value="performance" className="mt-3 space-y-4">
              <PerfTable rows={detail.employee_performance} title="Employee performance (Acoustic) — FY, value in Cr" testId="ob-perf-acoustic" />
              <PerfTable rows={detail.employee_performance_export} title="Employee performance (Export) — FY, value in Cr" testId="ob-perf-export" />
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 items-start">
                <PairList title="Dealer vs Direct (Cr)" rows={detail.dealer_split} testId="ob-dealer-split" />
                <PairList title="Dealer performance FY (Cr)" rows={detail.dealer_values} testId="ob-dealer-values" />
                <PairList title="Industry performance FY (Cr)" rows={detail.industry_values} testId="ob-industry-values" />
              </div>
              {!!detail.top_orders?.length && (
                <Card className="overflow-hidden" data-testid="ob-top-orders">
                  <p className="px-4 pt-3 pb-1 text-sm font-heading font-semibold">Top 10 orders FY</p>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader><TableRow className="bg-slate-50">
                        {["Client", "Project", "Value (Cr)", "%", "Employee"].map((h) => (
                          <TableHead key={h} className="text-[11px] uppercase tracking-wider text-slate-500">{h}</TableHead>))}
                      </TableRow></TableHeader>
                      <TableBody>
                        {detail.top_orders.map((r, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-xs max-w-[200px]">{r.client}</TableCell>
                            <TableCell className="text-xs max-w-[220px] text-slate-500">{r.project}</TableCell>
                            <TableCell className="font-mono text-xs text-right">{cr(r.value_cr)}</TableCell>
                            <TableCell className="font-mono text-xs text-right">{cr(r.pct)}</TableCell>
                            <TableCell className="text-xs">{r.employee}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </Card>
              )}
              {!!detail.export_clients?.length && (
                <Card className="overflow-hidden" data-testid="ob-export-clients">
                  <p className="px-4 pt-3 pb-1 text-sm font-heading font-semibold">Export clients FY (Cr)</p>
                  <Table>
                    <TableHeader><TableRow className="bg-slate-50">
                      {["Client", "Country", "Value (Cr)", "%"].map((h) => (
                        <TableHead key={h} className="text-[11px] uppercase tracking-wider text-slate-500">{h}</TableHead>))}
                    </TableRow></TableHeader>
                    <TableBody>
                      {detail.export_clients.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs">{r.client}</TableCell>
                          <TableCell className="text-xs text-slate-500">{r.country}</TableCell>
                          <TableCell className="font-mono text-xs text-right">{cr(r.value_cr)}</TableCell>
                          <TableCell className="font-mono text-xs text-right">{cr(r.pct)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
